import { useState, useEffect } from "react";
import { FiX } from "react-icons/fi";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../../auth/AuthProvider";
import { Button } from "../../../components";
import { addOffset, updateOffset } from "../../../api/loads";
import type {
  LoadOffset,
  NGTurbineType,
  SourceType,
} from "../domain/types";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  loadId: string;
  /** Pass an existing offset to edit; omit to create new */
  initial?: LoadOffset | null;
}

const SOURCE_OPTIONS: { value: SourceType; label: string }[] = [
  { value: "solar", label: "Solar PV" },
  { value: "wind", label: "Wind" },
  { value: "ng", label: "Natural Gas" },
  { value: "bess", label: "Battery (BESS)" },
];

const NG_TURBINE_OPTIONS: { value: NGTurbineType; label: string }[] = [
  { value: "ccgt", label: "Combined Cycle (CCGT)" },
  { value: "scgt", label: "Simple Cycle (SCGT)" },
  { value: "reciprocating", label: "Reciprocating Engine" },
];

/**
 * Add or edit an on-site offset for a Load (RE/BESS/NG).
 *
 * Engine treats offsets as residual reducers in front of the grid: whatever
 * the dispatch can serve on-site reduces the residual that's attributed to
 * the grid's marginal mix. Pure grid-tied (no offsets) is the canonical case;
 * this modal is for layering offsets when modeling mixed datacenters.
 */
export function LoadOffsetModal({ isOpen, onClose, loadId, initial }: Props) {
  const { token } = useAuth();
  const qc = useQueryClient();
  const editing = !!initial;

  const [name, setName] = useState("");
  const [type, setType] = useState<SourceType>("solar");
  const [capacity, setCapacity] = useState<number>(50);
  const [ngTurbine, setNgTurbine] = useState<NGTurbineType>("ccgt");
  const [bessDuration, setBessDuration] = useState<number>(4);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (initial) {
      setName(initial.offset_name);
      setType(initial.source_type);
      setCapacity(Number(initial.nameplate_capacity_mw));
      if (initial.ng_turbine_type) setNgTurbine(initial.ng_turbine_type);
      if (initial.bess_duration_hours)
        setBessDuration(Number(initial.bess_duration_hours));
    } else {
      setName("");
      setType("solar");
      setCapacity(50);
      setNgTurbine("ccgt");
      setBessDuration(4);
    }
  }, [initial, isOpen]);

  if (!isOpen) return null;

  const handleSubmit: React.FormEventHandler = async (e) => {
    e.preventDefault();
    if (!token) return;
    if (!name.trim()) return setError("Offset name is required");
    if (capacity <= 0) return setError("Capacity must be > 0");
    setSubmitting(true);
    setError(null);
    try {
      const payload: Partial<LoadOffset> = {
        offset_name: name.trim(),
        source_type: type,
        nameplate_capacity_mw: capacity,
        sam_profile_flag: type === "solar" || type === "wind",
        tech_type_id:
          type === "solar"
            ? "solar_ground"
            : type === "wind"
              ? "wind_onshore"
              : null,
        ng_turbine_type: type === "ng" ? ngTurbine : null,
        bess_duration_hours: type === "bess" ? bessDuration : null,
      };
      if (editing && initial) {
        await updateOffset(loadId, initial.offset_id, payload, token);
      } else {
        await addOffset(loadId, payload, token);
      }
      qc.invalidateQueries({ queryKey: ["load", loadId] });
      onClose();
    } catch (e: any) {
      setError(e?.message || "Failed to save offset");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="bg-surface-700 rounded-lg shadow-xl border border-border w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="text-lg font-medium text-text-primary">
            {editing ? "Edit Offset" : "Add Offset"}
          </h2>
          <button onClick={onClose} className="text-text-muted hover:text-text-primary">
            <FiX size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-300 text-sm rounded p-3">
              {error}
            </div>
          )}

          <label className="block">
            <span className="block text-xs text-text-muted mb-1">Offset name</span>
            <input
              className="w-full bg-surface-600 border border-border rounded px-3 py-2 text-text-primary"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. On-site solar array A"
              required
            />
          </label>

          <div className="grid grid-cols-2 gap-3">
            <label>
              <span className="block text-xs text-text-muted mb-1">Type</span>
              <select
                className="w-full bg-surface-600 border border-border rounded px-3 py-2 text-text-primary"
                value={type}
                onChange={(e) => setType(e.target.value as SourceType)}
                disabled={editing}
              >
                {SOURCE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span className="block text-xs text-text-muted mb-1">
                Nameplate capacity (MW)
              </span>
              <input
                type="number"
                min="0"
                step="0.1"
                className="w-full bg-surface-600 border border-border rounded px-3 py-2 text-text-primary"
                value={capacity}
                onChange={(e) => setCapacity(Number(e.target.value))}
                required
              />
            </label>
          </div>

          {type === "ng" && (
            <label className="block">
              <span className="block text-xs text-text-muted mb-1">
                Turbine type
              </span>
              <select
                className="w-full bg-surface-600 border border-border rounded px-3 py-2 text-text-primary"
                value={ngTurbine}
                onChange={(e) => setNgTurbine(e.target.value as NGTurbineType)}
              >
                {NG_TURBINE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
          )}

          {type === "bess" && (
            <label className="block">
              <span className="block text-xs text-text-muted mb-1">
                Duration (hours)
              </span>
              <input
                type="number"
                min="0.5"
                step="0.5"
                className="w-full bg-surface-600 border border-border rounded px-3 py-2 text-text-primary"
                value={bessDuration}
                onChange={(e) => setBessDuration(Number(e.target.value))}
              />
            </label>
          )}

          <div className="flex items-center justify-end gap-2 pt-2 border-t border-border">
            <Button variant="secondary" type="button" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={submitting}>
              {submitting ? "Saving…" : editing ? "Save" : "Add Offset"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
